/**
 * Operations (SPEC §5.8). The op log is the sync protocol, the undo history and the audit trail.
 * Structural functions return `OpBody[]`; the client stamps the envelope (id, client, seq, ts).
 */
import type { NodeId } from './ids.ts'
import type { Node } from './model.ts'

export type OpBody =
  | { type: 'node.create'; nodeId: NodeId; payload: { node: Node } }
  | { type: 'node.content'; nodeId: NodeId; payload: { content: string } }
  | { type: 'node.move'; nodeId: NodeId; payload: { parentId: NodeId; order: string } }
  | { type: 'node.collapse'; nodeId: NodeId; payload: { collapsed: boolean } }
  /** Page/tag title (unique per graph, case-insensitive; uniqueness is checked by the server). */
  | { type: 'node.title'; nodeId: NodeId; payload: { title: string | null } }
  /** One field value (per-field last writer wins, SPEC §12); `value: null` removes the field. */
  | { type: 'node.props'; nodeId: NodeId; payload: { field: string; value: unknown } }
  /** The full tag list (explicit tags plus those derived from content). */
  | { type: 'node.tags'; nodeId: NodeId; payload: { tags: NodeId[] } }
  /**
   * Rewrite refs to the title `from` as refs to `to` in the node's current content (renames, D57).
   * Applied against whatever content the receiver has, so concurrent edits survive; the inverse
   * is an exact content op.
   */
  | { type: 'node.relink'; nodeId: NodeId; payload: { from: string; to: string } }
  /** Cascades to live descendants at application time. */
  | { type: 'node.delete'; nodeId: NodeId; payload: Record<string, never> }
  /** Restores exactly `ids` (the set a delete removed), so earlier tombstones stay deleted. */
  | { type: 'node.restore'; nodeId: NodeId; payload: { ids: NodeId[] } }

export type OpType = OpBody['type']

/** Ops that change tree shape or visibility (the UI re-flattens only for these). */
export const STRUCTURAL: ReadonlySet<OpType> = new Set([
  'node.create',
  'node.move',
  'node.collapse',
  'node.delete',
  'node.restore',
])

export interface Op {
  readonly id: string
  readonly clientId: string
  readonly clientSeq: number
  readonly ts: number
  readonly body: OpBody
}

export const op = {
  create: (node: Node): OpBody => ({ type: 'node.create', nodeId: node.id, payload: { node } }),
  content: (nodeId: NodeId, content: string): OpBody => ({ type: 'node.content', nodeId, payload: { content } }),
  move: (nodeId: NodeId, parentId: NodeId, order: string): OpBody => ({
    type: 'node.move',
    nodeId,
    payload: { parentId, order },
  }),
  collapse: (nodeId: NodeId, collapsed: boolean): OpBody => ({ type: 'node.collapse', nodeId, payload: { collapsed } }),
  title: (nodeId: NodeId, title: string | null): OpBody => ({ type: 'node.title', nodeId, payload: { title } }),
  prop: (nodeId: NodeId, field: string, value: unknown): OpBody => ({ type: 'node.props', nodeId, payload: { field, value } }),
  tags: (nodeId: NodeId, tags: NodeId[]): OpBody => ({ type: 'node.tags', nodeId, payload: { tags } }),
  relink: (nodeId: NodeId, from: string, to: string): OpBody => ({ type: 'node.relink', nodeId, payload: { from, to } }),
  delete: (nodeId: NodeId): OpBody => ({ type: 'node.delete', nodeId, payload: {} }),
  restore: (nodeId: NodeId, ids: NodeId[]): OpBody => ({ type: 'node.restore', nodeId, payload: { ids } }),
}
